/**
 * @fileoverview Canonical public URLs for storefront legal and commerce policies.
 * @description Single source of truth for policy links consumed by JSON-LD,
 * the merchant feed, footer navigation, and Merchant Center operator runbooks.
 * @module legal/policy-urls
 */

import { siteConfig } from "~/lib/config/site"

/**
 * Absolute URLs for crawlable policy pages required by Google Merchant Center
 * and consumer-trust best practices.
 *
 * @typedef {Object} PolicyUrls
 * @property {string} returns - Return and refund policy page.
 * @property {string} shipping - Shipping and delivery policy page.
 * @property {string} terms - Terms of Service page.
 * @property {string} privacy - Privacy Policy page.
 * @property {string} dataPrivacy - Data Processing Addendum page.
 * @property {string} privacyChoices - Privacy Choices / consent controls page.
 * @property {string} license - Framework software license agreement page.
 *
 * @important `dataPrivacy` and `privacyChoices` are defined here for canonical
 * URL composition but are not yet consumed by JSON-LD or the merchant feed —
 * only `returns` is wired today (via `buildMerchantReturnPolicy` in
 * `src/lib/ads-framework/seo.ts`). Add consumers when structured data or feeds
 * need DPA / Privacy Choices links; until then the keys prevent URL drift.
 */
export const POLICY_URLS = {
  returns: `${siteConfig.url}${siteConfig.baseLinks.returns}`,
  shipping: `${siteConfig.url}${siteConfig.baseLinks.shipping}`,
  terms: `${siteConfig.url}${siteConfig.baseLinks.terms}`,
  privacy: `${siteConfig.url}${siteConfig.baseLinks.privacy}`,
  license: `${siteConfig.url}${siteConfig.baseLinks.license}`,
  dataPrivacy: `${siteConfig.url}${siteConfig.baseLinks.dataPrivacy}`,
  privacyChoices: `${siteConfig.url}${siteConfig.baseLinks.privacyChoices}`,
} as const

/**
 * Merchant Center return-policy label for full framework SKUs (30-day window).
 * Matches the default return policy configured in Merchant Center.
 */
export const MC_RETURN_POLICY_LABEL_DEFAULT = "default" as const

/**
 * Merchant Center return-policy label for skills-only zip bundles.
 * Maps to an exception policy with no guaranteed return window.
 */
export const MC_RETURN_POLICY_LABEL_SKILLS = "skills-discretionary" as const
